/** Sign in / create account / reset password, plus "try the demo". */
import { useState, type ReactElement } from 'react';
import { Icon } from '../components/Icon';
import { Spinner } from '../components/ui';
import { config } from '../config';
import { getSupabase } from '../data/supabaseClient';
import { errorMessage, log } from '../lib/logger';

type Mode = 'signin' | 'signup' | 'reset';

export function LoginPage({ onDemo }: { onDemo: () => void }): ReactElement {
  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    setError(null); setInfo(null);
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) { setError('Enter a valid email address.'); return; }
    if (mode !== 'reset' && password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setBusy(true);
    const sb = getSupabase();
    try {
      if (mode === 'signin') {
        const { error: e } = await sb.auth.signInWithPassword({ email: email.trim(), password });
        if (e) throw e;
        log.info('auth.signed_in', {});
      } else if (mode === 'signup') {
        const { data, error: e } = await sb.auth.signUp({ email: email.trim(), password, options: { data: { display_name: name.trim() || undefined }, emailRedirectTo: window.location.origin } });
        if (e) throw e;
        if (!data.session) setInfo('Check your email to confirm your account, then sign in.');
      } else {
        const { error: e } = await sb.auth.resetPasswordForEmail(email.trim(), { redirectTo: window.location.origin });
        if (e) throw e;
        setInfo('If that email has an account, a reset link is on its way.');
      }
    } catch (err) {
      const msg: string = errorMessage(err);
      log.warn('auth.failed', { mode, message: msg });
      setError(msg === 'Invalid login credentials' ? 'Email or password is wrong.' : msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth">
      <div className="auth-hero">
        <img className="auth-logo" src={`${import.meta.env.BASE_URL}peanut.png`} alt="" />
        <div className="brand" style={{ padding: 0 }}><span className="brand-mark brand-mascot"><img src={`${import.meta.env.BASE_URL}peanut-128.png`} alt="" /></span>{config.appName}</div>
        <h1>Food, body and training in one place.</h1>
        <p>Log a meal by describing it or snapping a photo. Watch your real burn get measured from your own data, not guessed.</p>
        <div className="auth-feats">
          <div><span className="qa-icon" style={{ background: 'var(--carbs)', width: 34, height: 34 }}><Icon name="sparkle" size={17} /></span>AI meal logging you can edit before saving</div>
          <div><span className="qa-icon" style={{ background: 'var(--protein)', width: 34, height: 34 }}><Icon name="trend" size={17} /></span>Observed TDEE and goal projections</div>
          <div><span className="qa-icon" style={{ background: 'var(--fat)', width: 34, height: 34 }}><Icon name="dumbbell" size={17} /></span>Lifting engine coming next</div>
        </div>
      </div>
      <div className="auth-form">
        <form className="auth-card" onSubmit={(e) => { e.preventDefault(); void submit(); }}>
          <h2>{mode === 'signin' ? 'Welcome back' : mode === 'signup' ? 'Create your account' : 'Reset password'}</h2>
          {mode === 'signup' && <label className="field"><span>Name</span><input className="input" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} autoComplete="name" /></label>}
          <label className="field"><span>Email</span><input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" autoFocus /></label>
          {mode !== 'reset' && <label className="field"><span>Password</span><input className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete={mode === 'signup' ? 'new-password' : 'current-password'} /></label>}
          {error !== null && <div className="note err">{error}</div>}
          {info !== null && <div className="note info">{info}</div>}
          <button type="submit" className="btn btn-primary" disabled={busy} style={{ padding: 12 }}>{busy && <Spinner />}{mode === 'signin' ? 'Sign in' : mode === 'signup' ? 'Create account' : 'Send reset link'}</button>
          <div className="row" style={{ justifyContent: 'space-between', fontSize: 14 }}>
            {mode === 'signin'
              ? (config.allowSignup ? <button type="button" className="link-btn" onClick={() => setMode('signup')}>Create an account</button> : <span className="faint" style={{ fontWeight: 600 }}>Private beta: invite only</span>)
              : <button type="button" className="link-btn" onClick={() => setMode('signin')}>Back to sign in</button>}
            {mode === 'signin' && <button type="button" className="link-btn" onClick={() => setMode('reset')}>Forgot password?</button>}
          </div>
          <div className="divider">OR</div>
          <button type="button" className="btn" onClick={onDemo} style={{ padding: 12 }}><Icon name="sparkle" size={18} /> Explore with sample data</button>
          <p className="faint" style={{ fontSize: 12.5, margin: 0, textAlign: 'center' }}>The demo runs in your browser only. Nothing is saved.</p>
        </form>
      </div>
    </div>
  );
}
